import { Component, Vue } from 'vue-facing-decorator';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Files, Check, Delete, EditPen, CirclePlusFilled } from '@element-plus/icons-vue';
import Header from '@/components/Header/Header.vue';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import ApiService from '@/services/ApiService';
import AuthService from '@/services/AuthService';

@Component(
{
    components:
    {
        Header,
        Breadcrumbs,
        Navigation,
		Files,
		Check,
		EditPen,
		Delete,
		CirclePlusFilled
    },
    enums:
    {
        NavigationIndex
    }
})

export default class Client extends Vue 
{
    private activeName : string = "1";  
    private isCustomer : boolean = true;
    private clientModelList : ClientModel[] = [];
    private customerModelList : CustomerModel[] = [];
    private tableData: object[] = [];
    private loading : boolean = true;
    private formValid : boolean = false;
    private hasPermission: boolean[] = [] as boolean[];
    private toDelete : ClientModel | undefined;
    private newClientModel : ClientModel = {} as ClientModel;
	private currentClient : ClientModel | undefined;
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;
    private allowedPrefixChars : string = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    private rules =
    {
        customerid:
        [
            {
                required: true,
                message: 'Bitte wählen Sie einen Mandanten aus.',
                trigger: 'blur'
            }
        ],
        name:
        [
            {
                required: true,
                message: 'Bitte geben Sie eine Bezeichnung ein.',
                trigger: 'blur'
            }
        ],
        tokenprefix:
        [
            {
                required: true,
                message: 'Bitte geben Sie ein Präfix für Ticklisten ein.',
                trigger: 'blur'
            }
        ],
        email:
        [
            {
                required: true,
                message: 'Bitte geben Sie eine gültige E-Mail ein.',
                trigger: 'blur'
            }
        ]
    };

    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['CLNT_SELECT'] = AuthService.hasPermission("CLNT_SELECT");
        this.hasPermission['CLNT_INSERT'] = AuthService.hasPermission("CLNT_INSERT");
        this.hasPermission['CLNT_UPDATE'] = AuthService.hasPermission("CLNT_UPDATE");
        this.hasPermission['CLNT_DELETE'] = AuthService.hasPermission("CLNT_DELETE");

        // check if user is a customer
        this.isCustomer = await AuthService.isCustomer();
        
        // load client list and customers dropdown
        if (this.hasPermission['CLNT_SELECT'])
        {
            await this.loadList();
            await this.loadCustomerList();
        }
        
        window.addEventListener('resize', this.onWindowResize);

        this.loading = false;
    }

    private beforeUnmount()
    {
        window.removeEventListener('resize', this.onWindowResize);
    }

	private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private async loadList()
    {
        this.loading = true;
        this.clientModelList = await ApiService.getAllClients() as ClientModel[];
        this.loading = false;
    }

    private async loadCustomerList()
    {
        this.loading = true;
        this.customerModelList = await ApiService.getAllCustomers() as CustomerModel[];
        this.loading = false;
        
        if (this.customerModelList && this.customerModelList.length > 0)
        {
            this.newClientModel.customerid = this.customerModelList[0].customerid;
        }
    }

    private async createClient()
    {
        // send new client model via request to API and await
        let client = await ApiService.createClient(this.newClientModel);

        // check if successful
        if (client)
        {
			ElMessage(
            {
               	message: "Neues Amt erfolgreich erstellt.",
               	type: 'success',
            });

            this.activeName = "1";
            this.newClientModel = {} as ClientModel;
            this.formValid = false;
            await this.loadList();
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Beim Erstellen des neuen Amts ist ein Fehler aufgetreten.",
               	type: 'error',
            });
        }
    }

    private async deleteClient(client : ClientModel)
    {
        let ok = await ApiService.deleteClient(client);

        if (ok)
        {
			ElMessage(
            {
               	message: "Amt wurde gelöscht.",
               	type: 'success',
            });
            await this.loadList();
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Amt konnte nicht gelöscht werden.",
               	type: 'error',
            });
        }
    }

    private validateEmail()
    {
        if (!this.newClientModel.email || this.newClientModel.email.length <= 0) return false;

        let re : RegExp = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
        return re.test(this.newClientModel.email);
    }

    private validateForm() : boolean
    {
        if (!this.validateEmail()) return false;
        if (this.newClientModel.customerid <= 0) return false;
        if (!this.newClientModel.name || this.newClientModel.name.length <= 0) return false;
        if (!this.newClientModel.tokenprefix || this.newClientModel.tokenprefix.length <= 0) return false;
        if (!this.newClientModel.email || this.newClientModel.email.length <= 0) return false;
        if (this.newClientModel.zipcode && this.newClientModel.zipcode.length > 0 && isNaN(this.newClientModel.zipcode)) return false;


        if (!this.newClientModel.tokenprefix || this.newClientModel.tokenprefix.length < 2) return false;
        for (let c of this.newClientModel.tokenprefix)
        {
            if (this.allowedPrefixChars.indexOf(c) < 0) return false;
        }

        return true;
    }

    private onInput()
    {
        if (this.newClientModel.tokenprefix)
        {
            this.newClientModel.tokenprefix = this.newClientModel.tokenprefix.toUpperCase();
        }
        this.formValid = this.validateForm();
    }

    private onClientEditClick(client : ClientModel)
    {
        this.$router.push(`/client/${client.clientid}`)
    }

    private onDeleteConfirm(action : string)
    {
        if (this.toDelete && action == 'confirm')
        {
            this.deleteClient(this.toDelete);
        }
    }

    private onClientDeleteClick(client : ClientModel)
    {
        this.toDelete = client;

        ElMessageBox.confirm('Soll diese Quelle wirklich gelöscht werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onDeleteConfirm,
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        });
    }

    private onNewClientClick()
    {
        this.createClient();
    }
}
